import { cn } from "@/common/utils/cn";
import { useMemo } from "react";
import { directiveEntryFormSteps } from "../constants/directiveEntryFormSteps.constant";
import { useCreateForwardingsFormStore } from "../stores/createForwardingsFormStore";
import { MaintenanceForwardingsDirectiveFormStepState } from "../types/maintenanceforwardingsDirectiveFormStep.type";
import { MaintenanceForwardingsRequiredDirectiveStepsData } from "../types/maintenanceForwardingsShowDirectiveDetails.type";

interface CreateForwardingDirectiveEntryFormStepProps<K extends keyof MaintenanceForwardingsRequiredDirectiveStepsData> {
  directiveEntryData: typeof directiveEntryFormSteps[keyof typeof directiveEntryFormSteps];
  attributes: MaintenanceForwardingsRequiredDirectiveStepsData[K] | undefined;
  index: number;
}

const CreateForwardingDirectiveEntryFormStep = <K extends keyof MaintenanceForwardingsRequiredDirectiveStepsData>({
  directiveEntryData,
  attributes,
  index
}: CreateForwardingDirectiveEntryFormStepProps<K>) => {
  const { currentStep } = useCreateForwardingsFormStore();

  const state = useMemo(() => {
    if (index > currentStep) return MaintenanceForwardingsDirectiveFormStepState.Pending;
    if (index === currentStep) return MaintenanceForwardingsDirectiveFormStepState.Current;
    return MaintenanceForwardingsDirectiveFormStepState.Completed;
  }, [index, currentStep]);

  const StepComponent = useMemo(() => {
    if (state === 'completed') {
      return directiveEntryData?.components.completed;
    }

    return directiveEntryData?.components.pending;
  }, [state]);

  if (!StepComponent) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        'min-w-10 min-h-10 max-w-10 max-h-10 flex items-center justify-center rounded-full',
        {
          [MaintenanceForwardingsDirectiveFormStepState.Pending]: 'bg-yellow-op-2',
          [MaintenanceForwardingsDirectiveFormStepState.Current]: 'bg-primary-blue',
          [MaintenanceForwardingsDirectiveFormStepState.Completed]: 'bg-success',
        }[state],
      )}>
        {index + 1}
      </div>

      <StepComponent
        stepIndex={index}
        state={state}
        attributes={attributes as never}
      />
    </div>
  );
}

export default CreateForwardingDirectiveEntryFormStep;
